import { Injectable, NgZone } from '@angular/core';
import { interval, Subscription } from 'rxjs';
import { UserActivityService } from './user-activity.service';
import { MessageBoxService } from './message-box.service';
import { AuthService } from './auth.service';
import { environment } from '../../../environments/environment';

@Injectable({ providedIn: 'root' })
export class SessionManagerService {
  private checkSub?: Subscription;
  private refreshInProgress = false;
  private sessionExpiredHandled = false;

  // Load timers from environment
  private readonly ACTIVITY_WINDOW_MS  = environment.session.ACTIVITY_WINDOW_MS;
  private readonly REFRESH_INTERVAL_MS = environment.session.REFRESH_INTERVAL_MS;
  private readonly CHECK_INTERVAL_MS   = environment.session.CHECK_INTERVAL_MS;
  

  // last successfully refreshed
  private lastRefreshTime: number | null = null;

  constructor(
    private authService: AuthService,
    private activityService: UserActivityService,
    private zone: NgZone,
    private messageBox: MessageBoxService
  ) {}

  start(): void {
    if (this.checkSub) {
      return;
    }

    this.sessionExpiredHandled = false;
    this.refreshInProgress = false;
    this.lastRefreshTime = Date.now(); //  login is the 1st "refresh"


    this.zone.runOutsideAngular(() => {
      this.checkSub = interval(this.CHECK_INTERVAL_MS).subscribe(() => {
        this.checkSession();
      });
    });
  }

  stop(): void {
    this.checkSub?.unsubscribe();
    this.checkSub = undefined;
  }

  private checkSession(): void {
    const token = this.authService.getAccessToken();
    if (!token || token.trim().length === 0) {
      return;
    }

    const exp = this.authService.getAccessTokenExpiry();
    if (!exp) {
      return;
    }

    const now = Date.now();
    const timeToExpiry = exp - now;
    const lastActivity = this.activityService.getLastActivityTime();
    const idleTime = now - lastActivity;

      // Token Expired : show logout dialog and logout
    if (timeToExpiry <= 0) {
      if (this.sessionExpiredHandled) {
        return;
      }
      this.sessionExpiredHandled = true;
      this.stop();
      this.zone.run(() => {
        this.messageBox
          .showMessage({
            title: 'Logged Out',
            message: 'You have been logged out due to inactivity. Click OK to log in again.',
            warning: true,
            alignMessage: 'center',
            alignTitle: 'center'
          })
          .subscribe(() => {
            this.authService.logout(false);
          });
      });

      return;
    }

    // refresh every 5 minutes when user is active

    const userRecentlyActive = idleTime <= this.ACTIVITY_WINDOW_MS;
    const timeSinceLastRefresh =
      this.lastRefreshTime ? now - this.lastRefreshTime : Number.MAX_SAFE_INTEGER;
    if (
      userRecentlyActive &&
      timeSinceLastRefresh >= this.REFRESH_INTERVAL_MS &&
      !this.refreshInProgress
    ) {
      this.refreshInProgress = true;
      this.authService.refreshToken().subscribe({
        next: (ok) => {
          this.refreshInProgress = false;
          if (ok) {
            // update when refreshed successfully
            this.lastRefreshTime = Date.now();
          } else {
            // refresh failed : force logout
            this.zone.run(() => this.authService.logout(false));
          }
        },
        error: (err) => {
          console.error('SessionManager refreshToken() ERROR:', err);
          this.refreshInProgress = false;
          this.zone.run(() => this.authService.logout(false));
        }
      });
    } else if (!userRecentlyActive) {
      
    }
  }
}
